import React, { useEffect, useRef, useState, useMemo } from "react";
import { motion, useInView, useMotionValue, animate, useReducedMotion } from "framer-motion";

/** Animated number counter — counts from 0 to `value` once it enters the viewport. */
export function CountUp({ value = 0, duration = 1.4, suffix = "", prefix = "", className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const reduce = useReducedMotion();
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(() => {
    // If motion is reduced, render the final value immediately.
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      const isFloat = String(value).includes(".");
      return isFloat ? Number(value).toFixed(1) : Math.round(Number(value) || 0).toLocaleString();
    }
    return "0";
  });

  useEffect(() => {
    if (reduce) {
      const isFloat = String(value).includes(".");
      setDisplay(isFloat ? Number(value).toFixed(1) : Math.round(Number(value) || 0).toLocaleString());
      return;
    }
    if (!inView) return;
    const controls = animate(mv, Number(value) || 0, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => {
        const isFloat = String(value).includes(".");
        setDisplay(isFloat ? v.toFixed(1) : Math.round(v).toLocaleString());
      },
    });
    return controls.stop;
  }, [inView, value, reduce]);

  return (
    <span ref={ref} className={className}>
      {prefix}{display}{suffix}
    </span>
  );
}

/**
 * Editorial fade-up wrapper.
 * - Never animates from opacity 0 (starts at 0.35 so content is always readable).
 * - Short duration (0.32s) so cards don't look like loading skeletons.
 * - Small y translate (8px) — subtle premium reveal.
 * - Honors `prefers-reduced-motion: reduce` → renders instantly with no transform.
 */
export const FadeUp = ({ children, delay = 0, className = "", ...rest }) => {
  const reduce = useReducedMotion();
  if (reduce) {
    return <div className={className} {...rest}>{children}</div>;
  }
  return (
    <motion.div
      initial={{ opacity: 0.35, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-5% 0px" }}
      transition={{ duration: 0.32, delay: Math.min(delay, 0.12), ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

/** Horizontal bar — animates width on enter. */
export function AnimatedBar({ pct = 0, color = "#0A0A0A", height = 6, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  return (
    <div ref={ref} className={`w-full bg-zinc-100 overflow-hidden ${className}`} style={{ height }}>
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: inView ? `${Math.max(0, Math.min(100, pct))}%` : 0 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        style={{ height: "100%", background: color }}
      />
    </div>
  );
}

/** Funnel step bar that scales horizontally. */
export function FunnelStep({ label, value, total, color = "#0A0A0A" }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="grid grid-cols-[1fr_auto] gap-3 items-baseline">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs uppercase tracking-[0.18em] text-zinc-600">{label}</span>
        <span className="text-xs uppercase tracking-[0.12em] text-zinc-400">{Math.round(pct)}%</span>
      </div>
      <div className="serif text-xl">{Number(value).toLocaleString()}</div>
      <div className="col-span-2"><AnimatedBar pct={pct} color={color} height={8} /></div>
    </div>
  );
}
